'use client';
/**
 * LE CONTRÔLE ANNUEL DE LA PRATIQUE — `BIO24-F-PROC-01 §Maintien des compétences`.
 *
 * « Tant qu'un personnel habilité pratique régulièrement sur le périmètre de son
 *   habilitation, ses compétences sont présumées maintenues. La pratique est
 *   mesurée via l'inscription effective sur le planning. **Le planning du
 *   personnel a valeur d'enregistrement qualité à ce titre.** »
 *
 * C'était la seule exigence de la procédure que rien ne couvrait : le planning
 * et les habilitations ne se parlaient que dans un sens — le blocage.
 *
 * ── ⚠️ CET ÉCRAN NE PRONONCE RIEN ─────────────────────────────────────────
 *
 * Il CONSTATE. La réhabilitation est une décision de la Direction, sur avis du
 * RT et du RQ — un écran qui la déclencherait retirerait un droit sans que
 * personne l'ait décidé. On rend donc une liste de personnes à convoquer, pas
 * une liste de personnes déchues.
 *
 * ── ⚠️ SANS SEUIL, LE CONTRÔLE NE CONCLUT RIEN ────────────────────────────
 *
 * Et il le DIT, en tête, en ambre. La tentation serait d'afficher « conforme »
 * faute d'écart constaté : c'est exactement l'inverse: aucun écart n'a pu être
 * cherché. Un contrôle annuel qui conclut sans critère est pire qu'un contrôle
 * absent — il s'imprime, et il se présente en audit.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { card, inputStyle, EmptyState, Skeleton } from '@/components/ui';
/* L'en-tête passe par `EnteteEcran` et non par un `TitrePage` composé à la
   main : un cliquet du dépôt compte les écrans qui composent encore leur
   entête, et il refuse que ce nombre monte. */
import { EnteteEcran } from '@/components/EnteteEcran';
import { fetchEcriture } from '@/lib/ecrire';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
type Verdict = 'maintenue' | 'a_reevaluer' | 'indecidable';

interface Constat {
  orgId: string; nom: string;
  habilitation: string; libelle: string;
  jours: number; seuil: number | null;
  verdict: Verdict; motif: string;
}

interface Reponse {
  fenetre: { du: string; au: string };
  seuils: Record<string, number>;
  habilitations: { code: string; libelle: string }[];
  constats: Constat[];
  bilan: { maintenues: number; aReevaluer: number; indecidables: number; resume: string };
}

const TEINTE: Record<Verdict, { fond: string; trait: string; mot: string }> = {
  maintenue: { fond: 'var(--green-50)', trait: 'var(--green-600)', mot: 'maintenue' },
  a_reevaluer: { fond: 'var(--amber-50)', trait: 'var(--amber-600)', mot: 'à réévaluer' },
  indecidable: { fond: 'var(--gray-25)', trait: 'var(--text-muted)', mot: 'indécidable' },
};

export function RhPratiqueEcran() {
  const { session, showToast } = useGed();
  const [d, setD] = React.useState<Reponse | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [tour, setTour] = React.useState(0);
  const [brouillon, setBrouillon] = React.useState<Record<string, string> | null>(null);
  const [enCours, setEnCours] = React.useState(false);
  const estRQ = (session.roles || []).includes('rq');

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/pratique', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((x) => { if (vivant) setD(x as Reponse); })
      /* Une erreur de lecture ne doit pas se lire « aucun écart » : c'est
         exactement le faux vide que cet écran existe pour éviter. */
      .catch(() => { if (vivant) setErreur('Le contrôle n’a pas pu être calculé : la liste ci-dessous serait fausse.'); });
    return () => { vivant = false; };
  }, [tour]);

  const enregistrer = async () => {
    if (!brouillon) return;
    setEnCours(true);
    /* ⚠️ `fetchEcriture`, jamais `fetch` : toute écriture passe par lui, et un
       contrôle du dépôt le vérifie. C'est ce qui garantit qu'aucune écriture
       n'échappe au témoin. */
    const r = await fetchEcriture('/api/ged/ged/rh/pratique/seuils', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      /* `base` (14/10/2026) : les seuils lus à l'ouverture du brouillon — le
         serveur n'applique que ceux que l'on a changés (fusion à trois voies). */
      body: JSON.stringify({ seuils: brouillon, base: d?.seuils || {} }),
    });
    setEnCours(false);
    if (!r.ok) {
      const j = await r.json().catch(() => null);
      showToast(j?.error?.message || 'Enregistrement refusé', 'erreur');
      return;
    }
    setBrouillon(null);
    setTour((n) => n + 1);
    showToast('Seuils enregistrés.');
  };

  if (erreur) {
    return (
      <section>
        <div style={{ ...card, padding: 16, background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>⚠ {erreur}</strong>
        </div>
      </section>
    );
  }
  if (!d) return <div style={{ ...card, padding: 0 }}><Skeleton rows={6} height={36} /></div>;

  const sansSeuil = d.habilitations.filter((h) => d.seuils[h.code] === undefined);
  const aReevaluer = d.constats.filter((c) => c.verdict === 'a_reevaluer');
  const autres = d.constats.filter((c) => c.verdict !== 'a_reevaluer');

  return (
    <section>
      <EnteteEcran rubrique={<>Plannings &amp; RH</>} clauseISO="6.2" titre="Maintien des compétences"
        sousTitre={<>
          La pratique se mesure sur le <strong>planning</strong>, qui a valeur d’enregistrement qualité
          à ce titre. Un constat ne prononce rien : la <strong>réhabilitation est décidée par la
          Direction</strong>, sur avis du responsable technique et du responsable qualité.
        </>} />

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 14px' }}>
        Période contrôlée : <strong>{d.fenetre.du}</strong> → <strong>{d.fenetre.au}</strong> ·
        {' '}{d.bilan.resume}
      </p>

      {/* ⚠️ L'AVERTISSEMENT VIENT EN TÊTE, avant tout résultat. Sans seuil, le
          contrôle n'a rien contrôlé — et une page qui commence par « 12
          maintenues » se lit comme un contrôle réussi. */}
      {!!sansSeuil.length && (
        <div style={{ ...card, padding: 16, marginBottom: 14, background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
          <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>
            {sansSeuil.length} habilitation(s) n’ont aucun seuil de pratique
          </strong>
          <p style={{ fontSize: 12.5, margin: '5px 0 0', lineHeight: 1.55 }}>
            Le contrôle ne peut RIEN en conclure, et <strong>ce n’est pas un constat de
            conformité</strong>. La procédure confie ce seuil au responsable technique, en
            concertation avec le responsable qualité : il tient compte de la nature des actes
            (manuels ou automatisés), de leur fréquence et du niveau à maintenir.
            <br />
            {sansSeuil.map((h) => h.libelle).join(' · ')}
          </p>
        </div>
      )}

      {/* ── LES SEUILS ─────────────────────────────────────────────────────── */}
      <div style={{ ...card, padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <strong style={{ flex: 1, fontSize: 13.5 }}>Seuils de pratique — jours sur douze mois</strong>
          {estRQ && !brouillon && (
            <Bouton taille="sm" onClick={() => setBrouillon(Object.fromEntries(
                d.habilitations.map((h) => [h.code, d.seuils[h.code] === undefined ? '' : String(d.seuils[h.code])]),
              ))}>Modifier</Bouton>
          )}
        </div>
        {!estRQ && (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px' }}>
            Consultable — fixer un seuil relève du RQ : il décide qui sera convoqué à une réhabilitation.
          </p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
          {d.habilitations.map((h) => {
            const v = brouillon ? brouillon[h.code] : (d.seuils[h.code] === undefined ? '' : String(d.seuils[h.code]));
            return (
              <label key={h.code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <span style={{ flex: 1, color: v ? 'var(--text-strong)' : 'var(--amber-600)' }}>{h.libelle}</span>
                {brouillon ? (
                  <input type="number" min={1} value={v} placeholder="—"
                    style={{ ...inputStyle, width: 74, height: 30 }}
                    onChange={(e) => setBrouillon({ ...brouillon, [h.code]: e.target.value })} />
                ) : (
                  <span style={{ fontWeight: 700, color: v ? 'var(--text-strong)' : 'var(--amber-600)' }}>
                    {v ? `${v} j` : 'non fixé'}
                  </span>
                )}
              </label>
            );
          })}
        </div>
        {brouillon && (
          <>
            {/* ⚠️ Vider un seuil ne vaut pas zéro : « zéro jour » se lirait
                « toujours maintenue », l'inverse d'un seuil non fixé. */}
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
              Laisser une case vide <strong>retire</strong> le seuil : l’habilitation redevient
              indécidable. Ce n’est pas « zéro jour », qui se lirait « toujours maintenue ».
            </p>
            <PiedActions taille="sm" style={{ marginTop: 10 }} annuler={{ onClick: () => setBrouillon(null) }} principale={{ libelle: 'Enregistrer les seuils', libelleChargement: 'Enregistrement…', chargement: enCours, disabled: enCours, onClick: () => void enregistrer() }} />
          </>
        )}
      </div>

      {/* ── CE QUI APPELLE UNE DÉCISION, EN PREMIER ────────────────────────── */}
      {!!aReevaluer.length && (
        <div style={{ ...card, padding: 0, marginBottom: 14, overflow: 'hidden', border: '1px solid var(--amber-500)' }}>
          <div style={{ padding: '12px 16px', background: 'var(--amber-50)' }}>
            <strong style={{ fontSize: 13, color: 'var(--amber-600)' }}>
              {aReevaluer.length} compétence(s) à réévaluer
            </strong>
            <p style={{ fontSize: 12.5, margin: '3px 0 0', color: 'var(--text-body)' }}>
              La pratique n’est pas attestée sur la période. La Direction décide de la réhabilitation,
              selon les phases 4 et 5 de la procédure.
            </p>
          </div>
          {aReevaluer.map((c, i) => <Ligne key={`${c.orgId}|${c.habilitation}`} c={c} premier={!i} />)}
        </div>
      )}

      {!d.constats.length ? (
        <EmptyState titre="Aucune habilitation à contrôler"
          indice="Personne ne détient d’habilitation active sur cette période." />
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {autres.map((c, i) => <Ligne key={`${c.orgId}|${c.habilitation}`} c={c} premier={!i} />)}
        </div>
      )}
    </section>
  );
}

/** Une ligne de constat. Le MOTIF est rendu tel quel : c'est lui qu'on relit en
 *  revue, et il dit ce que le nombre seul ne dit pas. */
function Ligne({ c, premier }: { c: Constat; premier: boolean }) {
  const t = TEINTE[c.verdict];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 16px',
      borderTop: premier ? 'none' : '1px solid var(--border-default)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--text-strong)' }}>
          {c.nom} — <strong>{c.libelle}</strong>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.5 }}>{c.motif}</div>
      </div>
      <span style={{ flex: 'none', fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)',
        padding: '2px 9px', background: t.fond, color: t.trait }}>{t.mot}</span>
    </div>
  );
}
